import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import {
  StickyNote, User as UserIcon, Clock,
  ChevronLeft, ChevronRight, ListChecks, Circle, Award,
} from "lucide-react";
import { addDays, startOfWeek, pad, toLocalIso } from "@/utils/status";
import { useOutcomes } from "@/context/OutcomesContext";
import { useAuth } from "@/context/AuthContext";

function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function fmtDayHeader(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString("it-IT", {
    weekday: "long", day: "2-digit", month: "long",
  });
}

const PRESETS = [
  { key: "today",    label: "Oggi" },
  { key: "week",     label: "Settimana" },
  { key: "month",    label: "Mese" },
  { key: "past",     label: "Passati" },
];

export default function WorkflowPage({ calendarType = "consulenti" }) {
  const { user } = useAuth();
  const { statusesFor, metaFor, resultsFor, resultMetaFor, isCompletedStatus } = useOutcomes();
  const [users, setUsers] = useState([]);
  const [types, setTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [preset, setPreset] = useState("today");
  const [dateRef, setDateRef] = useState(new Date());
  const [collabId, setCollabId] = useState("");
  const [appointments, setAppointments] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState({});

  const rolesFilter = calendarType === "trainers"
    ? "manager,trainer"
    : "manager,collaborator";
  const isTrainerCal = calendarType === "trainers";

  useEffect(() => {
    setCollabId("");
    setAppointments([]);
  }, [calendarType]);

  useEffect(() => {
    if (!collabId && user?.id) setCollabId(user.id);
  }, [user, collabId]);

  const canSwitchCollab = user?.role === "admin" || user?.role === "manager" || user?.role === "receptionist"
    || (calendarType === "trainers" && user?.role !== "trainer")
    || (user?.role === "collaborator" && calendarType === "trainers");

  const range = useMemo(() => {
    const d = new Date(dateRef);
    d.setHours(0, 0, 0, 0);
    if (preset === "today") return { start: d, end: addDays(d, 1), label: d.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long", year: "numeric" }) };
    if (preset === "week") {
      const s = startOfWeek(d);
      return { start: s, end: addDays(s, 7), label: `Sett. ${s.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} – ${addDays(s, 6).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}` };
    }
    if (preset === "month") {
      const s = new Date(d.getFullYear(), d.getMonth(), 1);
      const e = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      return { start: s, end: e, label: s.toLocaleDateString("it-IT", { month: "long", year: "numeric" }) };
    }
    const s = addDays(d, -30);
    return { start: s, end: addDays(d, 1), label: `Ultimi 30 giorni` };
  }, [preset, dateRef]);

  const load = useCallback(async () => {
    if (!collabId) return;
    try {
      const params = {
        start: toLocalIso(range.start),
        end: toLocalIso(range.end),
        collaborator_ids: collabId,
        calendar: calendarType,
      };
      const { data } = await api.get("/appointments", { params });
      setAppointments(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [range, collabId, calendarType]);

  useEffect(() => {
    api.get("/users", { params: { roles: rolesFilter, calendar: calendarType } }).then(({ data }) => {
      const active = data.filter(u => u.active);
      setUsers(active);
      setCollabId(prev => {
        if (prev && active.find(u => u.id === prev)) return prev;
        if (user?.id && active.find(u => u.id === user.id)) return user.id;
        return active[0]?.id || "";
      });
    });
    api.get("/appointment-types", { params: { calendar: calendarType } }).then(({ data }) => setTypes(data));
    api.get("/clients").then(({ data }) => setClients(data));
  }, [rolesFilter, user?.id, calendarType]);
  useEffect(() => { load(); }, [load]);

  const shiftBy = (n) => {
    const d = new Date(dateRef);
    if (preset === "today") setDateRef(addDays(d, n));
    else if (preset === "week") setDateRef(addDays(d, n * 7));
    else if (preset === "month") setDateRef(new Date(d.getFullYear(), d.getMonth() + n, 1));
  };

  const changeStatus = async (apt, newStatus) => {
    if (apt.status === newStatus) return;
    setBusy(b => ({ ...b, [apt.id]: true }));
    try {
      const { data } = await api.patch(`/appointments/${apt.id}`, { status: newStatus });
      setAppointments(list => list.map(a => a.id === apt.id ? data : a));
      toast.success(`Stato: ${metaFor(newStatus).label}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(b => { const { [apt.id]: _, ...rest } = b; return rest; }); }
  };

  const changeResult = async (apt, newResult) => {
    setBusy(b => ({ ...b, [apt.id]: true }));
    try {
      const { data } = await api.patch(`/appointments/${apt.id}`, { result: newResult || null });
      setAppointments(list => list.map(a => a.id === apt.id ? data : a));
      const label = newResult ? resultMetaFor(calendarType, newResult).label : "Nessuno";
      toast.success(`Esito: ${label}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(b => { const { [apt.id]: _, ...rest } = b; return rest; }); }
  };

  const saveNote = async (apt, note) => {
    try {
      await api.patch(`/appointments/${apt.id}`, { post_notes: note });
      setAppointments(list => list.map(a => a.id === apt.id ? { ...a, post_notes: note } : a));
      toast.success("Nota salvata");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const statusList = statusesFor(calendarType);
  const resultList = resultsFor(calendarType);
  const filtered = useMemo(() => {
    let list = [...appointments].sort((a, b) => new Date(a.start) - new Date(b.start));
    if (statusFilter !== "all") list = list.filter(a => a.status === statusFilter);
    return list;
  }, [appointments, statusFilter]);
  const groups = useMemo(() => {
    const g = {};
    filtered.forEach(a => {
      const day = a.start.slice(0, 10);
      (g[day] = g[day] || []).push(a);
    });
    return g;
  }, [filtered]);

  const total = appointments.length;
  const counts = useMemo(() => {
    const c = {};
    statusList.forEach(s => { c[s] = 0; });
    appointments.forEach(a => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [appointments, statusList]);
  const done = appointments.filter(a => a.status !== "da_svolgere").length;
  const progressPct = total ? Math.round((done / total) * 100) : 0;

  const currentUser = users.find(u => u.id === collabId);
  const nextTodo = appointments.filter(a => a.status === "da_svolgere" && new Date(a.end) >= new Date())
    .sort((a, b) => new Date(a.start) - new Date(b.start))[0];

  const highlightStatuses = ["effettuato", "no_show"];
  const highlight0 = metaFor(highlightStatuses[0]);
  const highlight1 = metaFor(highlightStatuses[1]);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="border-b bg-card px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-600/10 flex items-center justify-center">
              <ListChecks className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold tracking-tight">
                Flusso {isTrainerCal ? "Trainers" : "Consulenti"}
              </h1>
              <p className="text-xs text-muted-foreground">Segna Stato ed Esito in successione con un click.</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Select value={collabId} onValueChange={setCollabId} disabled={!canSwitchCollab}>
              <SelectTrigger className="w-56" data-testid="workflow-consultant-select">
                <SelectValue placeholder="Seleziona consulente" />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: u.color }} />
                      {u.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Tabs value={preset} onValueChange={setPreset}>
              <TabsList>
                {PRESETS.map(p => (
                  <TabsTrigger key={p.key} value={p.key} data-testid={`workflow-preset-${p.key}`}>{p.label}</TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {preset !== "past" && (
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" onClick={() => shiftBy(-1)} data-testid="workflow-prev-button">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setDateRef(new Date())}>Oggi</Button>
                <Button variant="outline" size="icon" onClick={() => shiftBy(1)} data-testid="workflow-next-button">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Card className="lg:col-span-2">
            <CardContent className="p-3 flex items-center gap-3">
              <div
                className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold shrink-0"
                style={{ backgroundColor: currentUser?.color || "#64748B" }}
              >
                {(currentUser?.name || "?").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Consulente</div>
                <div className="font-display font-bold text-sm truncate">{currentUser?.name || "—"}</div>
                <div className="text-[11px] text-muted-foreground truncate">{range.label}</div>
              </div>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardContent className="p-3">
              <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground">Avanzamento</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="font-display text-2xl font-bold">{done}<span className="text-muted-foreground">/{total}</span></span>
                <span className="text-xs text-muted-foreground">gestiti</span>
                <span className="ml-auto font-mono text-sm font-semibold" data-testid="workflow-progress-percent">{progressPct}%</span>
              </div>
              <Progress value={progressPct} className="mt-2 h-2" />
            </CardContent>
          </Card>

          <MiniStat label={highlight0.label} value={counts[highlightStatuses[0]] || 0} meta={highlight0} />
          <MiniStat label={highlight1.label} value={counts[highlightStatuses[1]] || 0} meta={highlight1} />
        </div>

        {nextTodo && (
          <div
            className="mt-3 rounded-lg border-l-4 bg-blue-50 px-4 py-2 flex items-center gap-3 text-sm"
            style={{ borderLeftColor: currentUser?.color || "#2563EB" }}
            data-testid="workflow-next-todo"
          >
            <div className="text-xs uppercase font-bold tracking-widest text-blue-800 shrink-0">Prossimo</div>
            <div className="font-medium text-blue-900 truncate">{nextTodo.title}</div>
            <span className="text-blue-700 font-mono text-xs whitespace-nowrap">
              {new Date(nextTodo.start).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" })} · {new Date(nextTodo.start).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
            </span>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setStatusFilter("all")}
            data-testid="workflow-filter-all"
            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${statusFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            Tutti · {total}
          </button>
          {statusList.map(s => {
            const meta = metaFor(s);
            const active = statusFilter === s;
            return (
              <button key={s}
                onClick={() => setStatusFilter(s)}
                data-testid={`workflow-filter-${s}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${active ? "text-white" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                style={active ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
                {meta.label} · {counts[s] || 0}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 bg-slate-50">
        {Object.keys(groups).length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <Circle className="h-8 w-8 mx-auto mb-2 opacity-40" />
            Nessun appuntamento nel periodo selezionato.
          </div>
        )}
        {Object.entries(groups).map(([day, list]) => (
          <div key={day} className="mb-6">
            <div className="sticky top-0 z-10 bg-slate-50 py-2 flex items-center gap-2">
              <div className="text-xs uppercase font-bold tracking-widest text-slate-700">
                {fmtDayHeader(day)}
              </div>
              <div className="flex-1 border-t border-slate-200" />
              <Badge variant="secondary" className="text-[10px]">{list.length}</Badge>
            </div>

            <div className="relative pl-6">
              <div className="absolute left-2 top-2 bottom-2 w-px bg-slate-200" />
              {list.map((a) => (
                <WorkflowRow
                  key={a.id}
                  apt={a}
                  user={currentUser}
                  types={types}
                  clients={clients}
                  onStatus={changeStatus}
                  onResult={changeResult}
                  onNote={saveNote}
                  busy={!!busy[a.id]}
                  statusList={statusList}
                  metaFor={metaFor}
                  resultList={resultList}
                  resultMetaFor={resultMetaFor}
                  isCompletedStatus={isCompletedStatus}
                  calendarType={calendarType}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MiniStat({ label, value, meta }) {
  const m = meta || { color: "#3B82F6", icon: () => null };
  const Icon = m.icon;
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-2">
          <div
            className="h-8 w-8 rounded-md flex items-center justify-center shrink-0"
            style={{ backgroundColor: m.color }}
          >
            <Icon className="h-4 w-4 text-white" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">{label}</div>
            <div className="font-display text-xl font-bold">{value}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkflowRow({ apt, user, types, clients, onStatus, onResult, onNote, busy, statusList, metaFor, resultList, resultMetaFor, isCompletedStatus, calendarType }) {
  const s = new Date(apt.start), e = new Date(apt.end);
  const meta = metaFor(apt.status);
  const Icon = meta.icon;
  const type = types.find(t => t.id === apt.appointment_type_id);
  const client = clients.find(c => c.id === apt.client_id);
  const isCancelled = apt.status === "annullato";
  const [noteDraft, setNoteDraft] = useState(apt.post_notes || "");
  const [notePopoverOpen, setNotePopoverOpen] = useState(false);
  const showResults = isCompletedStatus(apt.status);
  const currentResultMeta = apt.result ? resultMetaFor(calendarType, apt.result) : null;

  useEffect(() => { setNoteDraft(apt.post_notes || ""); }, [apt.post_notes]);

  return (
    <div className="relative mb-3" data-testid={`workflow-row-${apt.id}`}>
      <span
        className="absolute -left-6 top-6 h-3 w-3 rounded-full ring-4 ring-slate-50 shrink-0"
        style={{ backgroundColor: meta.color }}
      />

      <Card className={`transition-all ${busy ? "opacity-60" : ""} ${isCancelled ? "opacity-75" : ""}`}
            style={{ borderLeft: `4px solid ${user?.color || "#64748B"}` }}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className="inline-flex items-center justify-center h-6 w-6 rounded-md shrink-0 shadow-sm"
                  style={{ backgroundColor: meta.color }}
                  title={meta.label}
                >
                  <Icon className="h-3.5 w-3.5 text-white" strokeWidth={2.75} />
                </span>
                <div className={`font-display font-bold text-base ${isCancelled ? "line-through text-slate-500" : "text-slate-900"} truncate`}>
                  {apt.title}
                </div>
                {meta.strong && (
                  <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wide text-white" style={{ backgroundColor: meta.color, lineHeight: 1.4 }}>
                    {meta.label}
                  </span>
                )}
                {currentResultMeta && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wide text-white"
                    style={{ backgroundColor: currentResultMeta.color, lineHeight: 1.4 }}
                    data-testid={`workflow-result-badge-${apt.id}`}
                  >
                    <Award className="h-3 w-3" />
                    {currentResultMeta.label}
                  </span>
                )}
              </div>

              <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span className="font-mono">
                    {s.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}–{e.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </span>
                {type && (
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: type.color }} />
                    {type.name}
                  </span>
                )}
                {client && (
                  <span className="inline-flex items-center gap-1">
                    <UserIcon className="h-3 w-3" />
                    {client.name}
                  </span>
                )}
              </div>

              {apt.post_notes && (
                <div className="mt-2 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
                  <div className="text-[10px] uppercase font-bold tracking-widest text-amber-800 mb-0.5">Nota</div>
                  <div className="whitespace-pre-wrap">{apt.post_notes}</div>
                </div>
              )}
            </div>

            <div className="flex flex-col items-end gap-2 shrink-0">
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Popover open={notePopoverOpen} onOpenChange={setNotePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid={`workflow-note-button-${apt.id}`}>
                      <StickyNote className="h-3.5 w-3.5 mr-1.5" />
                      {apt.post_notes ? "Modifica nota" : "Nota"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="text-xs uppercase font-bold tracking-widest text-muted-foreground mb-2">Nota post-incontro</div>
                    <Textarea
                      rows={4}
                      value={noteDraft}
                      onChange={(e2) => setNoteDraft(e2.target.value)}
                      placeholder="Riepilogo, action items, prossimi passi…"
                      data-testid={`workflow-note-textarea-${apt.id}`}
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => { setNoteDraft(apt.post_notes || ""); setNotePopoverOpen(false); }}>Annulla</Button>
                      <Button size="sm" onClick={() => { onNote(apt, noteDraft); setNotePopoverOpen(false); }} data-testid={`workflow-note-save-${apt.id}`}>Salva</Button>
                    </div>
                  </PopoverContent>
                </Popover>

                <div className="flex items-center gap-1 bg-white border rounded-md p-0.5 shadow-sm">
                  {statusList.map(sk => {
                    const m = metaFor(sk);
                    const IconS = m.icon;
                    const active = apt.status === sk;
                    return (
                      <button
                        key={sk}
                        onClick={() => onStatus(apt, sk)}
                        disabled={busy}
                        title={m.label}
                        data-testid={`workflow-status-${sk}-${apt.id}`}
                        className={`h-8 px-2 rounded flex items-center gap-1.5 transition-all text-xs font-medium ${
                          active ? "text-white shadow-sm" : "text-slate-500 hover:bg-slate-100"
                        }`}
                        style={active ? { backgroundColor: m.color } : {}}
                      >
                        <IconS className="h-3.5 w-3.5" strokeWidth={active ? 2.75 : 2} />
                        <span className="hidden xl:inline">{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {showResults && (
                <div
                  className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 rounded-md p-1 shadow-sm"
                  data-testid={`workflow-result-row-${apt.id}`}
                >
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-widest text-emerald-800 px-1.5">
                    <Award className="h-3 w-3" /> Esito
                  </span>
                  <button
                    onClick={() => onResult(apt, null)}
                    disabled={busy}
                    data-testid={`workflow-result-none-${apt.id}`}
                    className={`h-7 px-2 rounded text-xs font-medium transition-all ${
                      !apt.result ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:bg-white"
                    }`}
                  >
                    —
                  </button>
                  {resultList.length === 0 ? (
                    <span className="text-[11px] text-muted-foreground italic px-2">Nessun esito configurato</span>
                  ) : (
                    resultList.map(rk => {
                      const rm = resultMetaFor(calendarType, rk);
                      const IconR = rm.icon;
                      const active = apt.result === rk;
                      return (
                        <button
                          key={rk}
                          onClick={() => onResult(apt, rk)}
                          disabled={busy}
                          title={rm.label}
                          data-testid={`workflow-result-${rk}-${apt.id}`}
                          className={`h-7 px-2 rounded flex items-center gap-1 transition-all text-xs font-medium ${
                            active ? "text-white shadow-sm" : "text-slate-500 hover:bg-white"
                          }`}
                          style={active ? { backgroundColor: rm.color } : {}}
                        >
                          <IconR className="h-3 w-3" strokeWidth={active ? 2.75 : 2} />
                          <span className="hidden xl:inline">{rm.label}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
